/**
 * advance-research.ts — per-tick research drain (M12 + MFix42.8b queue).
 *
 * Runs inside advanceTick AFTER advancePopulationAndEconomy (so any
 * research income that landed this tick is available to drain) and
 * BEFORE the per-planet / per-ship passes (so a tech completion this
 * tick is visible to downstream consumers — e.g. the combat pass
 * reading `resolveSlotWeapon` will pick up a just-completed weapon Mk).
 *
 * Per empire:
 *   1. Skip if no active research target.
 *   2. Look up the tech. If missing OR already in researchedTechs,
 *      defensively clear the slot and continue.
 *   3. Transfer RP from empire.resources.research to
 *      activeResearch.rpAccumulated, bounded by the remaining cost.
 *   4. On reaching researchCost, add the tech name to researchedTechs
 *      and clear the slot.
 *   5. MFix42.8b — if the empire has a non-empty researchQueue, shift
 *      entries off the head until a valid one promotes into the freed
 *      activeResearch slot (skip past invalid entries: already-researched
 *      / missing tech / race-gated / failed prereqs). Restore parked
 *      progress for the promoted tech if any.
 *
 * `researchTimeTicks` from the tech data is NOT honored in v1 per the
 * M12 design lock — completion is cost-only. The field remains on the
 * type for future balance passes.
 */

import type { Empire, GameState } from '../state/types/index';
import { EMPIRE_NEUTRAL_ID } from '../state/types/index';
import { effectiveMaxHull } from './research-effects';

/** Advance per-empire research by exactly one tick. Mutates state. */
export function advanceResearch(state: GameState): void {
  // Deterministic iteration: empire ids ascending.
  const empireIds = [...state.empires.keys()].sort((a, b) => a - b);

  for (const empireId of empireIds) {
    const empire = state.empires.get(empireId)!;
    // v3M21.1 — synthetic neutral empire never researches; the
    // activeResearch === null check below already short-circuits, but
    // skipping early keeps the intent obvious.
    if (empire.id === EMPIRE_NEUTRAL_ID) continue;
    if (empire.activeResearch === null) continue;

    const tech = state.techs.get(empire.activeResearch.techName);
    if (!tech) {
      // Data drift — shouldn't happen post-load. Defensive clear.
      empire.activeResearch = null;
      continue;
    }
    if (empire.researchedTechs.has(tech.techName)) {
      // Tech was added through some other path (future save-import).
      // Clear the now-pointless slot rather than re-researching.
      empire.activeResearch = null;
      continue;
    }

    // Transfer RP — bounded by both the empire's current RP bank and
    // the remaining cost. Negative RP (shouldn't happen) is clamped to 0.
    const remaining = tech.researchCost - empire.activeResearch.rpAccumulated;
    const available = Math.max(0, empire.resources.research);
    const transfer = Math.min(remaining, available);
    if (transfer > 0) {
      empire.activeResearch.rpAccumulated += transfer;
      empire.resources.research -= transfer;
    }

    // Complete — add to researched set, clear slot. researchCost can be
    // 0 (Mk 1 baseline techs); in that case we complete the first tick
    // the target is set regardless of RP balance.
    if (empire.activeResearch.rpAccumulated >= tech.researchCost) {
      // MFix7.2 — Hull-tech retro-fit. Snapshot every owned-ship's
      // effective max hull BEFORE the tech promotes; after promotion,
      // bump currentHull by exactly (newMax - oldMax) per the locked
      // semantics: "400/600 + Hull tech (+200 max) → 600/800". Skip
      // dead ships (don't resurrect via Hull research).
      // MFix13.1b — v4M5.1 collapsed Hull into the 'Survivability'
      // category. Hull-tech retrofit now keys off the sub-category
      // field instead of the legacy category string.
      const isHullTech = tech.subCategory === 'Hull';
      const preMaxByShipId = new Map<number, number>();
      if (isHullTech) {
        for (const shipId of empire.shipIds) {
          const ship = state.ships.get(shipId);
          if (!ship || ship.currentHull <= 0) continue;
          const tmpl = state.shipTemplates.get(
            `${empire.raceId}:${ship.chassis}`,
          );
          if (!tmpl) continue;
          preMaxByShipId.set(
            ship.id,
            effectiveMaxHull(state, empire, tmpl, ship),
          );
        }
      }

      empire.researchedTechs.add(tech.techName);
      empire.activeResearch = null;

      if (isHullTech) {
        for (const [shipId, oldMax] of preMaxByShipId) {
          const ship = state.ships.get(shipId);
          if (!ship || ship.currentHull <= 0) continue;
          const tmpl = state.shipTemplates.get(
            `${empire.raceId}:${ship.chassis}`,
          );
          if (!tmpl) continue;
          const newMax = effectiveMaxHull(state, empire, tmpl, ship);
          const delta = newMax - oldMax;
          if (delta > 0) ship.currentHull += delta;
        }
      }

      // MFix42.8b — research queue promotion. Shift the head into the
      // freshly-vacated activeResearch slot. The skip-past-invalid
      // pass handles entries that were enqueued earlier but have
      // since lost the right to promote (e.g. a parallel research
      // path completed the same tech via a chain, or a diplomacy
      // event changed the race gate). Helper is a no-op when the
      // queue is empty.
      promoteResearchQueueHead(state, empire);
    }

    // MFix1.1 — belt-and-suspenders clamp. The Math.min(remaining,
    // available) guard above can't push research below zero on its
    // own, but pinning the bank to >= 0 here documents the design
    // invariant ("research never goes negative") at the place
    // where someone would look first.
    if (empire.resources.research < 0) empire.resources.research = 0;
  }
}

/**
 * MFix42.8b — Promote the empire's research queue head into the freshly-
 * vacated `activeResearch` slot. Called from the completion branch in
 * `advanceResearch` once `empire.activeResearch === null` AND the
 * researched tech has been added to `empire.researchedTechs`. Safe to
 * call when activeResearch is non-null (no-op) — the function checks.
 *
 * Promotion rules (matches `applyEnqueueResearchTarget` gate semantics
 * plus a re-check at promotion time, since prereqs may have changed
 * between enqueue and promotion):
 *
 *   1. If activeResearch is non-null, return (slot already filled).
 *   2. Shift the head off the queue.
 *   3. If tech missing / already-researched / race-gated / failed
 *      prereqs → drop this entry, loop back to (2). The discarded
 *      tech does NOT get its parked entry consumed (it could
 *      legitimately be re-targeted later via a different prereq path).
 *   4. Otherwise: set activeResearch with parked-progress-or-zero RP
 *      (consuming the parked entry on success), return.
 *
 * Empty queue or queue with nothing valid → activeResearch stays null.
 */
function promoteResearchQueueHead(state: GameState, empire: Empire): void {
  if (empire.activeResearch !== null) return;

  while (empire.researchQueue.length > 0) {
    const next = empire.researchQueue.shift()!;
    const tech = state.techs.get(next);
    if (!tech) continue;
    if (empire.researchedTechs.has(next)) continue;
    if (tech.race !== 'All' && tech.race !== empire.raceId) continue;
    let prereqOk = true;
    for (const prereq of tech.prerequisites) {
      if (!empire.researchedTechs.has(prereq)) {
        prereqOk = false;
        break;
      }
    }
    if (!prereqOk) continue;

    const parked = empire.parkedResearchProgress.get(next);
    const startingRp = parked ?? 0;
    if (parked !== undefined) {
      empire.parkedResearchProgress.delete(next);
    }
    empire.activeResearch = { techName: next, rpAccumulated: startingRp };
    return;
  }
}
